/**
 * Diálogos propios de la aplicación.
 *
 * Reemplazan a `window.confirm` / `window.prompt` / `window.alert`, que el
 * navegador rotula con su propio origen («localhost:4173 dice…») sin permitir
 * cambiarlo. Además quedan con la marca, se ven igual en teléfono y en
 * computador, y no bloquean el hilo.
 *
 * Uso:
 *   const { confirmar, pedirTexto, avisar } = useDialogo();
 *   if (await confirmar({ mensaje: '¿Anular la guía?' })) { … }
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info, TriangleAlert } from 'lucide-react';
import { Boton } from './index';
import { cn } from '@/lib/cn';

type Tono = 'normal' | 'peligro';

interface OpcionesBase {
  titulo?: string;
  mensaje: ReactNode;
  aceptar?: string;
  cancelar?: string;
  tono?: Tono;
}

interface OpcionesTexto extends OpcionesBase {
  etiqueta?: string;
  valorInicial?: string;
  requerido?: boolean;
  marcador?: string;
}

type Peticion =
  | { tipo: 'confirmar'; op: OpcionesBase; resolver: (v: boolean) => void }
  | { tipo: 'texto'; op: OpcionesTexto; resolver: (v: string | null) => void }
  | { tipo: 'avisar'; op: OpcionesBase; resolver: (v: void) => void };

interface Contexto {
  confirmar: (op: OpcionesBase) => Promise<boolean>;
  pedirTexto: (op: OpcionesTexto) => Promise<string | null>;
  avisar: (op: OpcionesBase) => Promise<void>;
}

const Ctx = createContext<Contexto>({
  confirmar: async () => false,
  pedirTexto: async () => null,
  avisar: async () => {},
});

export function ProveedorDialogo({ children }: { children: ReactNode }) {
  const [peticion, setPeticion] = useState<Peticion | null>(null);
  const [texto, setTexto] = useState('');
  const campoRef = useRef<HTMLInputElement>(null);
  const aceptarRef = useRef<HTMLButtonElement>(null);

  const confirmar = useCallback(
    (op: OpcionesBase) =>
      new Promise<boolean>((resolver) => setPeticion({ tipo: 'confirmar', op, resolver })),
    [],
  );

  const pedirTexto = useCallback(
    (op: OpcionesTexto) =>
      new Promise<string | null>((resolver) => {
        setTexto(op.valorInicial ?? '');
        setPeticion({ tipo: 'texto', op, resolver });
      }),
    [],
  );

  const avisar = useCallback(
    (op: OpcionesBase) =>
      new Promise<void>((resolver) => setPeticion({ tipo: 'avisar', op, resolver })),
    [],
  );

  const cerrar = useCallback(
    (aceptado: boolean) => {
      if (!peticion) return;
      if (peticion.tipo === 'confirmar') peticion.resolver(aceptado);
      else if (peticion.tipo === 'texto') peticion.resolver(aceptado ? texto : null);
      else peticion.resolver();
      setPeticion(null);
      setTexto('');
    },
    [peticion, texto],
  );

  // Foco al abrir y cierre con Escape / Enter.
  useEffect(() => {
    if (!peticion) return;
    const t = setTimeout(() => {
      if (peticion.tipo === 'texto') campoRef.current?.focus();
      else aceptarRef.current?.focus();
    }, 40);

    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrar(false);
      }
      if (e.key === 'Enter' && peticion.tipo !== 'texto') {
        e.preventDefault();
        cerrar(true);
      }
    };
    window.addEventListener('keydown', alTeclado);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', alTeclado);
    };
  }, [peticion, cerrar]);

  const op = peticion?.op;
  const peligro = op?.tono === 'peligro';
  const requiereTexto =
    peticion?.tipo === 'texto' && (peticion.op.requerido ?? true) && !texto.trim();

  return (
    <Ctx.Provider value={{ confirmar, pedirTexto, avisar }}>
      {children}

      {peticion &&
        op &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialogo-titulo"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) cerrar(false);
            }}
          >
            <div className="metal w-full max-w-md rounded-tarjeta border border-linea shadow-tarjeta">
              {/* Encabezado con la marca: reemplaza al «localhost dice» del navegador */}
              <div className="flex items-center gap-2.5 border-b border-linea px-4 py-3">
                <img
                  src="/icons/icon-64.png"
                  alt=""
                  width={26}
                  height={26}
                  className="rounded-[7px] ring-1 ring-white/10"
                />
                <span className="text-sm font-semibold">Xtreme Mining dice:</span>
              </div>

              <div className="flex gap-3 px-4 py-4">
                <span
                  className={cn(
                    'mt-0.5 shrink-0',
                    peligro
                      ? 'text-fluor-rojo'
                      : peticion.tipo === 'avisar'
                        ? 'text-petroleo-300'
                        : 'text-fluor-amarillo',
                  )}
                >
                  {peligro ? (
                    <AlertTriangle size={20} />
                  ) : peticion.tipo === 'avisar' ? (
                    <Info size={20} />
                  ) : (
                    <TriangleAlert size={20} />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  {op.titulo && (
                    <h2 id="dialogo-titulo" className="mb-1 text-sm font-semibold">
                      {op.titulo}
                    </h2>
                  )}
                  <div className="text-sm leading-relaxed text-texto-mute">{op.mensaje}</div>

                  {peticion.tipo === 'texto' && (
                    <label className="mt-3 block">
                      {peticion.op.etiqueta && (
                        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
                          {peticion.op.etiqueta}
                        </span>
                      )}
                      <input
                        ref={campoRef}
                        value={texto}
                        placeholder={peticion.op.marcador}
                        onChange={(e) => setTexto(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !requiereTexto) cerrar(true);
                        }}
                        className="min-h-tactil w-full rounded-control border border-linea bg-petroleo-800/60 px-3 text-sm text-texto placeholder:text-texto-mute/50 focus:border-petroleo-300 focus:outline-none focus:ring-1 focus:ring-petroleo-300"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-linea px-4 py-3">
                {peticion.tipo !== 'avisar' && (
                  <Boton variante="fantasma" onClick={() => cerrar(false)}>
                    {op.cancelar ?? 'Cancelar'}
                  </Boton>
                )}
                <Boton
                  ref={aceptarRef}
                  variante={peligro ? 'peligro' : 'primario'}
                  onClick={() => cerrar(true)}
                  disabled={requiereTexto}
                >
                  {op.aceptar ?? (peticion.tipo === 'avisar' ? 'Entendido' : 'Aceptar')}
                </Boton>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Ctx.Provider>
  );
}

export function useDialogo() {
  return useContext(Ctx);
}
